<template>
  <div
    class="flex h-[100dvh] max-h-[100dvh] min-h-0 w-full overflow-hidden bg-[radial-gradient(120%_80%_at_0%_0%,#fff7ed_0%,transparent_55%),linear-gradient(180deg,#f8fafc_0%,#eef2f6_100%)] font-sans antialiased"
  >
    <!-- ============================================ -->
    <!-- SIDEBAR — modern minimal white               -->
    <!-- ============================================ -->
    <aside
      :class="[
        isExpanded ? 'w-[16.5rem]' : 'w-[4.5rem]',
        'relative z-30 flex h-full min-h-0 shrink-0 flex-col self-stretch transition-[width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]',
        'border-r border-slate-200/80 bg-white shadow-[1px_0_0_0_rgba(15,23,42,0.02)]',
      ]"
    >
      <!-- ============== Logo card ============== -->
      <div class="px-3 pb-2 pt-4">
        <div
          :class="[
            'flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60',
            isExpanded ? 'justify-start px-2.5 py-2' : 'justify-center px-1.5 py-2',
          ]"
        >
          <div
            class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg  p-1.5 shadow-sm shadow-orange-500/30"
          >
            <img
              src="@/assets/img/va.png"
              alt="VA House"
              class="h-full w-full object-contain"
            />
          </div>
          <div v-if="isExpanded" class="min-w-0 flex-1 text-left">
            <p class="truncate text-[13px] font-semibold tracking-tight text-slate-900">
              VA House Corp
            </p>
            <p class="truncate text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
              HR Portal
            </p>
          </div>
        </div>
      </div>

      <!-- ============== Header strip (label + toggle) ============== -->
      <div
        :class="[
          'flex items-center px-4 pb-1 pt-2',
          isExpanded ? 'justify-between' : 'justify-center',
        ]"
      >
        <p
          v-if="isExpanded"
          class="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400"
        >
          Modules
        </p>
        <button
          type="button"
          @click="toggleSidebar"
          class="group inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/40"
          :aria-expanded="isExpanded"
          :aria-label="isExpanded ? 'Collapse sidebar' : 'Expand sidebar'"
          :title="isExpanded ? 'Collapse sidebar' : 'Expand sidebar'"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="h-4 w-4 transition-transform duration-200"
            :class="{ 'rotate-180': !isExpanded }"
          >
            <path d="M15 6 9 12l6 6" />
          </svg>
        </button>
      </div>

      <!-- ============== Navigation ============== -->
      <nav
        :class="[
          'relative min-h-0 flex-1 space-y-0.5 px-2.5 pb-4 pt-1',
          isExpanded
            ? 'overflow-x-hidden overflow-y-auto sidebar-scroll'
            : 'overflow-hidden',
        ]"
      >
        <template v-for="item in filteredNav" :key="item.label">
          <!-- Single Item -->
          <router-link
            v-if="!item.children"
            :to="item.route"
            :title="isExpanded ? undefined : item.label"
            class="nav-item group relative flex items-center text-slate-600 transition duration-150"
            :class="
              isExpanded
                ? 'gap-3 rounded-lg px-2.5 py-2 hover:bg-slate-100/80 hover:text-slate-900'
                : 'mx-auto h-10 w-10 justify-center rounded-lg hover:bg-slate-100/80 hover:text-orange-600'
            "
            exact-active-class="sidebar-active"
          >
            <span
              v-if="item.icon"
              class="nav-icon-bg relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-slate-50 text-slate-500 transition duration-150 group-hover:bg-white group-hover:text-orange-600 group-hover:shadow-sm group-hover:ring-1 group-hover:ring-slate-200"
            >
              <icon :name="item.icon" class="text-[1.05rem]" />
              <span
                v-if="
                  item.route === '/client-module' &&
                  pendingClientApprovalNavCount > 0 &&
                  (userRole === 'ADMIN' || userRole === 'HR') &&
                  !isExpanded
                "
                class="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-white bg-amber-500 px-0.5 text-[10px] font-bold leading-none text-white"
                :title="`${pendingClientApprovalNavCount} client(s) pending admin approval`"
              >
                {{ pendingClientApprovalNavCount > 99 ? "99+" : pendingClientApprovalNavCount }}
              </span>
            </span>
            <span
              v-else
              class="h-8 w-8 shrink-0"
              aria-hidden="true"
            />
            <span
              v-if="isExpanded"
              class="min-w-0 flex-1 truncate text-[13px] font-medium tracking-tight"
              >{{ item.label }}</span
            >
            <span
              v-if="
                isExpanded &&
                item.route === '/client-module' &&
                pendingClientApprovalNavCount > 0 &&
                (userRole === 'ADMIN' || userRole === 'HR')
              "
              class="inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white"
              :title="`${pendingClientApprovalNavCount} client(s) pending admin approval`"
            >
              {{ pendingClientApprovalNavCount > 99 ? "99+" : pendingClientApprovalNavCount }}
            </span>

            <!-- Tooltip when collapsed -->
            <div
              v-show="!isExpanded"
              class="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 opacity-0 shadow-lg shadow-slate-300/30 transition-opacity duration-150 group-hover:opacity-100"
            >
              {{ item.label }}
            </div>
          </router-link>

          <!-- Item with Submenu -->
          <div v-else class="relative group">
            <button
              type="button"
              :title="isExpanded ? undefined : item.label"
              @click="toggleSubmenu(item.label)"
              :class="[
                'flex w-full items-center text-left text-slate-600 transition duration-150',
                isExpanded
                  ? 'justify-between gap-2 rounded-lg px-2.5 py-2 hover:bg-slate-100/80 hover:text-slate-900'
                  : 'mx-auto h-10 w-10 justify-center rounded-lg hover:bg-slate-100/80',
                submenuParentActiveClass(item),
              ]"
            >
              <div class="flex min-w-0 items-center gap-3">
                <span
                  class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-slate-50 text-slate-500 transition duration-150 group-hover:bg-white group-hover:text-orange-600 group-hover:shadow-sm group-hover:ring-1 group-hover:ring-slate-200"
                  :class="submenuParentIconClass(item)"
                >
                  <icon :name="item.icon" class="text-[1.05rem]" />
                </span>
                <span
                  v-if="isExpanded"
                  class="truncate text-[13px] font-medium tracking-tight"
                  >{{ item.label }}</span
                >
              </div>

              <svg
                v-if="isExpanded"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="h-3.5 w-3.5 flex-shrink-0 text-slate-400 transition-transform duration-200"
                :class="{ 'rotate-180 text-orange-500': openSubmenu === item.label }"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            <!-- Tooltip for collapsed sidebar -->
            <div
              v-show="!isExpanded"
              class="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 opacity-0 shadow-lg shadow-slate-300/30 transition-opacity duration-150 group-hover:opacity-100"
            >
              {{ item.label }}
            </div>

            <!-- Submenu (vertical guide line, no boxed panel) -->
            <transition name="slide-fade">
              <div
                v-if="openSubmenu === item.label && isExpanded"
                class="submenu-shell relative mb-1.5 ml-11 mt-1.5 flex flex-col gap-0.5 border-l-2 border-slate-200/75 pl-3"
              >
                <template v-for="(sub, i) in item.children" :key="i">
                  <button
                    v-if="
                      sub.route &&
                      canAccessSubmenuChild(sub) &&
                      isHrRequestsSubLocked(sub)
                    "
                    type="button"
                    class="subnav-link relative flex min-h-[2rem] w-full cursor-not-allowed items-center rounded-md py-1.5 pl-3 pr-2 text-left text-[12.5px] font-medium text-slate-400"
                    @click="notifyHrRequestsLocked"
                  >
                    <span class="min-w-0 truncate">{{ sub.label }}</span>
                    <span class="sr-only">(locked until profile approved)</span>
                  </button>
                  <router-link
                    v-else-if="sub.route && canAccessSubmenuChild(sub)"
                    :to="sub.route"
                    class="subnav-link relative flex min-h-[2rem] items-center rounded-md py-1.5 pl-3 pr-2 text-[12.5px] font-medium text-slate-500 transition-colors duration-150 hover:bg-slate-100/80 hover:text-slate-900"
                    active-class="subnav-active"
                  >
                    <span class="min-w-0 truncate">{{ sub.label }}</span>
                  </router-link>
                  <div
                    v-else-if="sub.section && sub.items"
                    class="mt-2 first:mt-0"
                  >
                    <p
                      class="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400"
                    >
                      {{ sub.section }}
                    </p>
                    <router-link
                      v-for="(link, j) in sub.items"
                      :key="j"
                      :to="link.route"
                      class="subnav-link relative block rounded-md py-1.5 pl-3 pr-2 text-[12.5px] font-medium text-slate-500 transition-colors duration-150 hover:bg-slate-100/80 hover:text-slate-900"
                      active-class="subnav-active"
                    >
                      {{ link.label }}
                    </router-link>
                  </div>
                </template>
              </div>
            </transition>
          </div>
        </template>
      </nav>

      <!-- ============== Footer ============== -->
      <div
        v-if="isExpanded"
        class="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-[10px] font-medium tracking-wide text-slate-400"
      >
        <span>© {{ currentYear }} VA House Corp</span>
        <span class="inline-flex items-center gap-1.5">
          <span class="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.55)]"></span>
          <span class="text-slate-500">Davao City</span>
        </span>
      </div>
      <div
        v-else
        class="flex items-center justify-center border-t border-slate-100 py-3"
        title="All systems operational"
      >
        <span class="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.55)]"></span>
      </div>
    </aside>

    <!-- =========== Main column =========== -->
    <div
      class="relative z-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden text-left"
    >
      <TopBarPage />
      <main class="min-h-0 w-full min-w-0 flex-1 overflow-y-auto overflow-x-hidden pb-8">
        <router-view></router-view>
      </main>
    </div>

    <!-- Provider: block navigation until profile is complete / resubmitted after HR return -->
    <Teleport to="body">
      <transition name="fade">
        <div
          v-if="showProviderProfileGuidanceModal"
          class="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="provider-pds-guidance-title"
          @click.self="closeProviderProfileGuidanceModal"
        >
          <div
            class="w-full max-w-md rounded-2xl border border-slate-200/90 bg-white p-6 shadow-2xl shadow-slate-400/20"
            @keydown.escape.stop.prevent="closeProviderProfileGuidanceModal"
          >
            <div
              class="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-600"
              aria-hidden="true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                class="h-5 w-5"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
            </div>
            <h2
              id="provider-pds-guidance-title"
              class="mt-4 text-lg font-semibold tracking-tight text-slate-900"
            >
              {{ providerProfileGuidanceCopy.title }}
            </h2>
            <p class="mt-2 text-sm leading-relaxed text-slate-600">
              {{ providerProfileGuidanceCopy.body }}
            </p>
            <div class="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                class="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                @click="closeProviderProfileGuidanceModal"
              >
                Stay on this page
              </button>
              <button
                type="button"
                class="rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-orange-500/25 transition hover:from-orange-600 hover:to-orange-700"
                @click="goToProviderProfileFromModal"
              >
                Go to provider profile
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import icon from "@/assets/icon.vue";
import TopBarPage from "../global/TopBarPage.vue";
import axios from "axios";
import { toast } from "vue3-toastify";
import { normalizePdsApprovalStatus } from "@/utils/pdsStatus";
import { mapState } from "pinia";
import { useFetchDataStore } from "@/store/pinia_store";

export default {
  name: "VirtualAssistantDashboard",
  components: { icon, TopBarPage },
  data() {
    return {
      isExpanded: true,
      openSubmenu: null,
      userRole: "",
      loggedInUser: null,
      providerEmployee: null,
      providerPdsNormalized: "Draft",
      providerOnboardingHydrated: false,
      showProviderProfileGuidanceModal: false,
      providerProfileGuidanceModalReason: "for_update",
      providerNavGuardTeardown: null,
      navItems: [
        {
          label: "Dashboard",
          icon: "home",
          route: "/dashboard",
          role: ["ADMIN", "HR", "VAM", "PROVIDER"],
        },
        {
          label: "Client List",
          icon: "client",
          route: "/client-module",
          role: ["ADMIN", "HR", "VAM"],
        },
        {
          label: "Onboard",
          icon: "onboard",
          route: "/onboard-module",
          role: ["ADMIN", "HR", "VAM", "PROVIDER"],
        },
        {
          label: "Service Providers",
          icon: "user",
          route: "/employee-module",
          role: ["ADMIN", "HR", "VAM"],
          children: [
            {
              label: "Manage Providers",
              route: "/manage-employee",
            },
            {
              label: "Attendance",
              route: "/attendance",
              role: ["ADMIN", "HR"],
            },
            {
              label: "Pending Accounts",
              route: "/user-account-module",
              role: ["ADMIN", "HR"],
            },
            {
              label: "Requests",
              route: "/manage-hr-requests",
              role: ["ADMIN", "HR"],
            },
          ],
        },
        
       
        {
          label: "Events Calendar",
          icon: "calendar",
          route: "/view-scheduled",
          role: ["ADMIN", "HR", "VAM"],
        },
        {
          label: "My Account",
          icon: "user",
          role: ["ADMIN", "HR", "VAM", "PROVIDER"],
          children: [
            { label: "Provider profile", route: "/view-my-details" },
            { label: "Attendance", route: "/attendance", role: ["PROVIDER"] },
            { label: "HR Requests", route: "/hr-requests" },
          ],
        },
        {
          label: "Site Admin",
          icon: "cog",
          role: ["ADMIN", "HR"],
          children: [
            { label: "Account & profile", route: "/settings/account" },
            {
              section: "Organization settings",
              items: [
                { label: "User roles & permissions", route: "/settings/roles" },
                { label: "Departments", route: "/settings/departments" },
                { label: "User management", route: "/settings/users" },
              ],
            },
            {
              section: "System",
              items: [{ label: "System logs", route: "/settings/logs" }],
            },
          ],
        },
      ],
    };
  },
  computed: {
    ...mapState(useFetchDataStore, ["client_details"]),
    /** ADMIN/HR: clients submitted by VAM awaiting approval (drives nav badge + bell count). */
    pendingClientApprovalNavCount() {
      if (this.userRole !== "ADMIN" && this.userRole !== "HR") return 0;
      return (this.client_details || []).filter((c) => {
        const s = String(c?.admin_approval_status ?? "Approved").trim();
        return s === "Pending";
      }).length;
    },
    filteredNav() {
      return this.navItems.filter((item) => item.role.includes(this.userRole));
    },
    currentYear() {
      return new Date().getFullYear();
    },
    providerProfileGuidanceCopy() {
      const r = this.providerProfileGuidanceModalReason;
      if (r === "rejected") {
        return {
          title: "Profile needs attention",
          body:
            "Your provider profile was rejected. Review HR feedback, update your profile under My Account, and resubmit before using other modules.",
        };
      }
      if (r === "incomplete") {
        return {
          title: "Profile not complete",
          body:
            "Finish your service provider profile (required fields and photo) under My Account before opening other areas of the portal.",
        };
      }
      return {
        title: "Profile needs updates",
        body:
          "HR returned your profile for updates. Open Service provider profile, apply the requested changes, and resubmit before continuing elsewhere in the system.",
      };
    },
  },
  watch: {
    $route: {
      immediate: true,
      async handler(to, from) {
        this.syncSubmenuWithRoute();
        if (
          this.userRole === "PROVIDER" &&
          this.loggedInUser &&
          to.path === "/view-my-details"
        ) {
          await this.refreshProviderEmployeeSnapshot(this.loggedInUser);
        }
        if (
          this.userRole === "PROVIDER" &&
          this.loggedInUser &&
          from?.path === "/view-my-details"
        ) {
          await this.enforceServiceProviderOnboarding(this.loggedInUser);
        }
      },
    },
  },
  methods: {
    canAccessSubmenuChild(child) {
      if (!Array.isArray(child?.role)) return true;
      return child.role.includes(this.userRole);
    },
    isSubmenuChildActive(item) {
      if (!item.children) return false;
      return item.children.some((c) => {
        if (c.route) {
          return this.canAccessSubmenuChild(c) && this.$route.path === c.route;
        }
        if (c.items?.length) {
          return c.items.some(
            (sub) => this.canAccessSubmenuChild(sub) && this.$route.path === sub.route,
          );
        }
        return false;
      });
    },
    submenuParentActiveClass(item) {
      if (!this.isSubmenuChildActive(item)) return "";
      return this.isExpanded
        ? "bg-orange-50/70 text-orange-900 shadow-[inset_2px_0_0_0_#f97316]"
        : "bg-orange-50 ring-1 ring-orange-200/80";
    },
    submenuParentIconClass(item) {
      if (!this.isSubmenuChildActive(item)) return "";
      return "!bg-white !text-orange-600 !ring-1 !ring-orange-200";
    },
    syncSubmenuWithRoute() {
      const match = this.filteredNav.find((item) => {
        if (!item.children) return false;
        return item.children.some((c) => {
          if (c.route) {
            return this.canAccessSubmenuChild(c) && c.route === this.$route.path;
          }
          if (c.items?.length) {
            return c.items.some(
              (sub) =>
                this.canAccessSubmenuChild(sub) &&
                sub.route === this.$route.path,
            );
          }
          return false;
        });
      });
      if (match) this.openSubmenu = match.label;
    },
    toggleSidebar() {
      this.isExpanded = !this.isExpanded;
    },
    toggleSubmenu(label) {
      if (!this.isExpanded) {
        this.isExpanded = true;
        setTimeout(() => {
          this.openSubmenu = label;
        }, 200);
      } else {
        this.openSubmenu = this.openSubmenu === label ? null : label;
      }
    },
    isProviderUser(user) {
      return String(user?.role_code || "")
        .trim()
        .toUpperCase() === "PROVIDER";
    },
    hasText(value) {
      return String(value ?? "").trim().length > 0;
    },
    isServiceProviderProfileComplete(user, employee) {
      const latestJob = Array.isArray(employee?.job_details)
        ? employee.job_details[0] || {}
        : {};

      const requiredValues = [
        user?.first_name,
        user?.last_name,
        user?.email,
        user?.employeeID,
        employee?.birthdate,
        employee?.contact_number,
        employee?.address,
        employee?.gender,
        latestJob?.position,
        latestJob?.job_title,
        latestJob?.contract,
        employee?.emergency_contact_name,
        employee?.emergency_contact_relation,
        employee?.emergency_contact_number,
      ];

      return requiredValues.every(this.hasText);
    },
    isHrRequestsSubLocked(sub) {
      return (
        sub.route === "/hr-requests" &&
        this.userRole === "PROVIDER" &&
        this.providerPdsNormalized !== "Approved"
      );
    },
    notifyHrRequestsLocked() {
      toast.info(
        "HR Requests unlock after your service provider profile is approved by HR. Complete your profile under My Account.",
      );
    },
    closeProviderProfileGuidanceModal() {
      this.showProviderProfileGuidanceModal = false;
    },
    goToProviderProfileFromModal() {
      this.showProviderProfileGuidanceModal = false;
      if (this.$route.path !== "/view-my-details") {
        this.$router.push("/view-my-details");
      }
    },
    async refreshProviderEmployeeSnapshot(user) {
      if (!this.isProviderUser(user) || user?.user_id == null) return;
      let employee = null;
      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/employee-details/user/${user.user_id}`,
          { withCredentials: true },
        );
        employee = Array.isArray(data) ? data[0] : data;
      } catch {
        employee = null;
      }
      this.providerEmployee = employee;
      this.providerPdsNormalized = normalizePdsApprovalStatus(
        employee?.pds_approval_status,
      );
    },
    /** @returns {null | 'for_update' | 'rejected' | 'incomplete'} */
    evaluateProviderProfileNavBlock(to) {
      if (!this.loggedInUser || !this.isProviderUser(this.loggedInUser)) {
        return null;
      }
      if (!this.providerOnboardingHydrated) return null;
      const path = to.path;
      if (path === "/view-my-details" || path === "/") return null;

      if (this.providerPdsNormalized === "For update") return "for_update";
      if (this.providerPdsNormalized === "Rejected") return "rejected";
      if (this.providerPdsNormalized === "Approved") return null;
      if (
        !this.isServiceProviderProfileComplete(
          this.loggedInUser,
          this.providerEmployee,
        )
      ) {
        return "incomplete";
      }
      return null;
    },
    async enforceServiceProviderOnboarding(user) {
      if (!this.isProviderUser(user)) return;

      await this.refreshProviderEmployeeSnapshot(user);

      if (this.$route.path === "/view-my-details") {
        return;
      }

      if (this.$route.path === "/hr-requests") {
        if (this.providerPdsNormalized !== "Approved") {
          this.$router.push("/view-my-details");
        }
        return;
      }

      if (
        this.providerPdsNormalized === "For update" ||
        this.providerPdsNormalized === "Rejected"
      ) {
        this.$router.push("/view-my-details");
        return;
      }

      if (this.providerPdsNormalized === "Approved") {
        return;
      }

      if (!this.isServiceProviderProfileComplete(user, this.providerEmployee)) {
        this.$router.push("/view-my-details");
      }
    },
    async fetchUser() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/auth/me`,
          {
            withCredentials: true,
          },
        );

        if (response.data) {
          this.loggedInUser = response.data;
          this.userRole = String(response.data.role_code || "")
            .trim()
            .toUpperCase();
          await this.enforceServiceProviderOnboarding(response.data);
          this.$nextTick(() => this.syncSubmenuWithRoute());
        } else {
          this.$router.push("/");
          location.reload();
        }
      } catch (error) {
        console.error("Failed to fetch user:", error);
        this.$router.push("/");
      } finally {
        this.providerOnboardingHydrated = true;
      }
    },
  },
  mounted() {
    this.providerNavGuardTeardown = this.$router.beforeEach(async (to, from, next) => {
      try {
        if (
          this.loggedInUser &&
          this.isProviderUser(this.loggedInUser) &&
          from.path === "/view-my-details" &&
          to.path !== "/view-my-details"
        ) {
          await this.refreshProviderEmployeeSnapshot(this.loggedInUser);
        }
        const reason = this.evaluateProviderProfileNavBlock(to);
        if (reason) {
          this.providerProfileGuidanceModalReason = reason;
          this.showProviderProfileGuidanceModal = true;
          next(false);
          return;
        }
        next();
      } catch {
        next();
      }
    });
    this.fetchUser();
  },
  beforeUnmount() {
    if (typeof this.providerNavGuardTeardown === "function") {
      this.providerNavGuardTeardown();
      this.providerNavGuardTeardown = null;
    }
  },
};
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Active primary nav item — soft orange tint, not a heavy gradient */
.sidebar-active {
  background-color: rgba(255, 247, 237, 0.95); /* orange-50 */
  color: #c2410c !important; /* orange-700 */
  box-shadow: inset 2px 0 0 0 #f97316;
  font-weight: 600;
}

.sidebar-active .nav-icon-bg {
  background-color: #ffffff !important;
  color: #ea580c !important; /* orange-600 */
  box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.02), 0 0 0 1px #fed7aa;
}

/* Active submenu link — clean inset bar */
.subnav-active {
  background-color: rgba(255, 247, 237, 0.85);
  color: #c2410c !important;
  font-weight: 600;
  box-shadow: inset 2px 0 0 0 #f97316;
}

.subnav-active .subnav-item-icon {
  color: #ea580c !important;
}

/* Custom thin scrollbar for the nav */
.sidebar-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.35) transparent;
}
.sidebar-scroll::-webkit-scrollbar {
  width: 6px;
}
.sidebar-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar-scroll::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.3);
  border-radius: 9999px;
}
.sidebar-scroll::-webkit-scrollbar-thumb:hover {
  background-color: rgba(249, 115, 22, 0.45);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.22s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
